import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { useAppSelector } from '..';

export type MetadataField = {
  value: string | Array<any> | null;
  isRequired: boolean;
  isHidden: boolean;
};

export type MetadatasType = Record<string, MetadataField>;

export type RoleMetadata = {
  id: string;
  role_id: string;
  metadatas_jsonb: MetadatasType;
  updated_at: string;
  required_metadatas: Array<string>;
  hidden_metadatas: Array<string>;
  visible_metadatas: Array<string>;
};

export type UserPreferences = {
  id: string;
  user_id: string;
  preferences_data: {
    richtext_plainpaste_enabled?: boolean;
  };
};

export type ConfigData = {
  metadata: RoleMetadata | null;
  userPreferences: UserPreferences | null;
};

export type ConfigState = {
  data: ConfigData;
  initialData: ConfigData;
  isLoading: boolean;
  hasError: boolean;
};

const initialState: ConfigState = {
  initialData: { metadata: null, userPreferences: null },
  data: { metadata: null, userPreferences: null },
  isLoading: false,
  hasError: false,
};

const extractMetadatasByFilter = (
  configData: RoleMetadata,
  filterFn: (field: MetadataField) => boolean,
): string[] => {
  return Object.entries(configData?.metadatas_jsonb || {})
    .filter(([_, value]) => filterFn(value))
    .map(([key]) => key);
};

const extractRequiredMetadatas = (configData: RoleMetadata): string[] =>
  extractMetadatasByFilter(configData, (v) => !v.isHidden && v.isRequired);

const extractHiddenMetadatas = (configData: RoleMetadata): string[] =>
  extractMetadatasByFilter(configData, (v) => v.isHidden);

const extractVisibleMetadatas = (configData: RoleMetadata): string[] =>
  extractMetadatasByFilter(configData, (v) => !v.isHidden);

const enrichMetadataConfig = (metadataConfig: RoleMetadata): RoleMetadata => ({
  ...metadataConfig,
  hidden_metadatas: extractHiddenMetadatas(metadataConfig),
  required_metadatas: extractRequiredMetadatas(metadataConfig),
  visible_metadatas: extractVisibleMetadatas(metadataConfig),
});

export const loadRoleMetadataConfig = createAsyncThunk(
  'config/loadRoleMetadata',
  async (userStrapiId: number) => {
    const res = await fetch('/api/graphql/getRoleMetadataByStrapiId', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variables: { _userStrapiId: userStrapiId } }),
    });
    const data = await res.json();

    if (data.data.role_default_metadata.length > 0) {
      return { metadataConfig: data.data.role_default_metadata[0] };
    }

    const userRoleRes = await fetch('/api/graphql/getUserRole', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variables: { userStrapiId } }),
    });
    const userRoleData = await userRoleRes.json();

    const roleId = userRoleData.data.user_role[0]?.role_id;
    if (!roleId) {
      throw new Error('Role ID not found for user');
    }

    const insertRes = await fetch('/api/graphql/insertRoleDefaultMetadata', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variables: { role_id: roleId } }),
    });
    const insertData = await insertRes.json();

    return {
      metadataConfig: insertData.data.insert_role_default_metadata.returning[0],
    };
  },
);

export const updateRoleMetadatas = createAsyncThunk(
  'config/updateRoleMetadata',
  async (params: { configId: string; metadatas: MetadatasType }) => {
    const res = await fetch('/api/graphql/updateRoleMetadata', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        variables: { id: params.configId, metadatas: params.metadatas },
      }),
    });
    const data = await res.json();

    return { metadataConfig: data.data.update_role_default_metadata_by_pk };
  },
);

export const loadUserPreferences = createAsyncThunk(
  'config/loadUserPreferences',
  async () => {
    try {
      const userAccess = await axios.post('/api/checkAccessToken');

      const res = await fetch('/api/graphql/getUserPreferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variables: { user_strapi_id: userAccess.data.id },
        }),
      });
      const data = await res.json();

      if (data.data.user_preferences[0]) {
        return { userPreferences: data.data.user_preferences[0] };
      }

      const insertRes = await fetch('/api/graphql/insertUserPreferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variables: { user_strapi_id: userAccess.data.id },
        }),
      });
      const insertData = await insertRes.json();

      return { userPreferences: insertData.data.user_preferences[0] };
    } catch (error) {
      return { userPreferences: null };
    }
  },
);

export const updateUserPreferences = createAsyncThunk(
  'config/updateUserPreferences',
  async (params: { preferences_data: UserPreferences['preferences_data'] }) => {
    try {
      const userAccess = await axios.post('/api/checkAccessToken');
      const res = await fetch('/api/graphql/updateUserPreferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variables: {
            user_strapi_id: userAccess.data.id,
            preferences_data: params.preferences_data,
          },
        }),
      });
      const data = await res.json();

      return { userPreferences: data.data.update_user_preferences_by_pk };
    } catch (error) {
      return { userPreferences: null };
    }
  },
);

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadRoleMetadataConfig.pending, (state) => {
        state.isLoading = true;
        state.hasError = false;
      })
      .addCase(loadRoleMetadataConfig.fulfilled, (state, action) => {
        const enrichedMetadata = enrichMetadataConfig(
          action.payload.metadataConfig,
        );
        state.isLoading = false;
        state.hasError = false;
        state.data = { ...state.data, metadata: enrichedMetadata };
        state.initialData = {
          ...state.initialData,
          metadata: enrichedMetadata,
        };
      })
      .addCase(loadRoleMetadataConfig.rejected, (state) => {
        state.isLoading = false;
        state.hasError = true;
      })
      .addCase(updateRoleMetadatas.pending, (state) => {
        state.isLoading = true;
        state.hasError = false;
      })
      .addCase(updateRoleMetadatas.fulfilled, (state, action) => {
        const enrichedMetadata = enrichMetadataConfig(
          action.payload.metadataConfig,
        );
        state.isLoading = false;
        state.hasError = false;
        state.data = { ...state.data, metadata: enrichedMetadata };
        state.initialData = {
          ...state.initialData,
          metadata: enrichedMetadata,
        };
      })
      .addCase(updateRoleMetadatas.rejected, (state) => {
        state.isLoading = false;
        state.hasError = true;
      })
      .addCase(loadUserPreferences.pending, (state) => {
        state.isLoading = true;
        state.hasError = false;
      })
      .addCase(loadUserPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasError = false;
        state.data = {
          ...state.data,
          userPreferences: action.payload.userPreferences,
        };
        state.initialData = {
          ...state.initialData,
          userPreferences: action.payload.userPreferences,
        };
      })
      .addCase(loadUserPreferences.rejected, (state) => {
        state.isLoading = false;
        state.hasError = true;
      })
      .addCase(updateUserPreferences.pending, (state) => {
        state.isLoading = true;
        state.hasError = false;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasError = false;
        state.data = {
          ...state.data,
          userPreferences: action.payload.userPreferences,
        };
        state.initialData = {
          ...state.initialData,
          userPreferences: action.payload.userPreferences,
        };
      })
      .addCase(updateUserPreferences.rejected, (state) => {
        state.isLoading = false;
        state.hasError = true;
      });
  },
});

export const config = configSlice.reducer;

export const configActions = configSlice.actions;

export const useConfig = () => {
  return useAppSelector((state) => {
    return state.config;
  });
};
